import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import {
  SquaresFour,
  Images,
  Tag,
  EnvelopeSimple,
  ShieldCheck,
  UsersThree,
  SignOut,
  ArrowUpRight,
} from "@phosphor-icons/react";

// `adminOnly: true` hides the item from staff editors
const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/admin/slides", label: "Slideshow", icon: Images, testid: "nav-slides" },
  { to: "/admin/offers", label: "Special Offers", icon: Tag, testid: "nav-offers" },
  { to: "/admin/contacts", label: "Enquiries", icon: EnvelopeSimple, testid: "nav-contacts" },
  { to: "/admin/audit", label: "Login Activity", icon: ShieldCheck, testid: "nav-audit", adminOnly: true },
  { to: "/admin/users", label: "Team", icon: UsersThree, testid: "nav-users", adminOnly: true },
];

function BuildFooter() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api
      .get("/health/build-info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(false));
  }, []);
  if (!info) return null;
  const built = info.build_time
    ? new Date(info.build_time).toLocaleString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
  return (
    <div
      className="px-4 pt-3 pb-1 text-[0.55rem] uppercase tracking-[0.18em] font-mono-accent text-[#5a6685] leading-loose"
      data-testid="admin-build-footer"
      title={`Started ${info.started_at}`}
    >
      <div>
        v{info.version} · <span className="text-[#a8b2cc]">{info.commit}</span>
      </div>
      <div className="text-[0.5rem] tracking-[0.16em]">{built}</div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, authChecked, logout } = useAuth();
  const navigate = useNavigate();

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070b14" }}>
        <div className="font-mono-accent text-[#a8b2cc]">Checking session…</div>
      </div>
    );
  }
  if (!user || !["admin", "editor"].includes(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }

  const isAdmin = user.role === "admin";
  const visibleNav = NAV.filter((n) => !n.adminOnly || isAdmin);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "#070b14" }}
      data-testid="admin-layout"
    >
      <aside
        className="w-[280px] fixed h-screen flex flex-col p-5"
        style={{ background: "#070b14" }}
      >
        <div className="relative rounded-[32px] h-full flex flex-col bg-[#141e32] text-white overflow-hidden">
          {/* Rainbow bar on top of sidebar */}
          <div className="absolute inset-x-0 top-0 rainbow-bar" />

          <div className="px-6 pt-10 pb-8">
            <Logo variant="light" />
          </div>

          <nav className="flex-1 px-4 space-y-1">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-full font-mono-accent text-[0.72rem] uppercase tracking-[0.18em] font-semibold transition-colors ${
                    isActive
                      ? "bg-[#f5ecd9] text-[#070b14]"
                      : "text-[#a8b2cc] hover:bg-[#141e32] hover:text-white"
                  }`
                }
              >
                <n.icon size={16} weight="duotone" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 space-y-2 border-t border-[#1f2a3f] mt-4">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-[#a8b2cc] hover:text-white font-mono-accent text-[0.7rem] uppercase tracking-[0.2em]"
              data-testid="admin-view-site"
            >
              <ArrowUpRight size={13} weight="bold" /> View site
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-full text-[#af3fa6] hover:bg-[#141e32] font-mono-accent text-[0.7rem] uppercase tracking-[0.2em]"
              data-testid="admin-logout-button"
            >
              <SignOut size={13} weight="bold" /> Sign out
            </button>
            <div className="px-4 pt-3 text-[0.7rem] text-[#a8b2cc] leading-relaxed">
              Signed in as<br />
              <span className="text-white">{user.email}</span>
              <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[0.55rem] uppercase tracking-[0.18em] font-nav font-semibold"
                    style={{
                      background: isAdmin ? "rgba(245,236,217,0.08)" : "rgba(221,166,50,0.12)",
                      color: isAdmin ? "#f5ecd9" : "#dda632",
                    }}>
                {user.role}
              </span>
            </div>
            <BuildFooter />
          </div>
        </div>
      </aside>

      <div className="flex-1 ml-[280px]">
        <Outlet />
      </div>
    </div>
  );
}
